function resolveApiUrl() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const isBrowser = typeof window !== 'undefined';
  const isLocalhost = isBrowser && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  if (isLocalhost) {
    return 'http://localhost:3001/api';
  }

  // Em produção sem variável, usa o mesmo domínio do frontend.
  return isBrowser ? `${window.location.origin}/api` : '/api';
}

export const API_URL = resolveApiUrl().replace(/\/$/, '');
export const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
const TOKEN_KEY = 'auth_token';

function parseJsonSafe(res) {
  return res.json().catch(() => ({}));
}

// As funções getStoredToken, setStoredToken e clearStoredToken são usadas para gerenciar o token JWT no armazenamento local do navegador (localStorage). Elas permitem obter, armazenar e remover o token conforme necessário para autenticação nas requisições à API.
export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// tokenOverride é um parâmetro opcional que permite passar um token específico para a função buildHeaders, em vez de usar o token armazenado. Isso pode ser útil em casos onde você deseja usar um token diferente para uma requisição específica, sem alterar o token armazenado globalmente.
// A função getAuthHeader é usada para construir o cabeçalho de autorização (Authorization) com o token JWT. Ela verifica se um token foi fornecido como argumento (tokenOverride) ou se há um token armazenado usando getStoredToken. Se um token estiver disponível, ele retorna um objeto com o cabeçalho Authorization formatado como "Bearer {token}". Caso contrário, retorna um objeto vazio.
function getAuthHeader(tokenOverride) {
  const token = tokenOverride || getStoredToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

// A função buildHeaders é usada para construir os cabeçalhos HTTP para as requisições à API. Ela aceita um objeto de opções que pode incluir includeJson (para indicar se o cabeçalho Content-Type: application/json deve ser incluído) e tokenOverride (para fornecer um token específico para a autenticação). A função retorna um objeto de cabeçalhos que combina os cabeçalhos de autenticação e, opcionalmente, o cabeçalho de tipo de conteúdo JSON.
function buildHeaders({ includeJson = false, tokenOverride } = {}) {
  return {
    ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    ...getAuthHeader(tokenOverride),
  };
}

async function request(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, options);

  if (options.expectBlob) {
    return {
      ok: res.ok,
      status: res.status,
      blob: await res.blob(),
      headers: res.headers,
    };
  }

  return parseJsonSafe(res);
}


// JSON.stringify é usado para converter um objeto JavaScript em uma string JSON, que pode ser enviada no corpo da requisição HTTP.
export async function apiLogin(email, password) {
  return request('/auth/login', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ email, password }),
  });
}

export async function apiRegister(email, password) {
  return request('/auth/register', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ email, password }),
  });
}

export async function apiVerifyEmail(email, code) {
  return request('/auth/verify-email', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ email, code }),
  });
}

export async function apiResendVerificationCode(email) {
  return request('/auth/resend-verification-code', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ email }),
  });
}

// A função apiGetMe é usada para fazer uma requisição GET para a rota /api/auth/me, que é uma rota protegida que retorna os dados do usuário autenticado. Ela aceita um token como argumento, que é passado para a função buildHeaders para incluir o cabeçalho de autenticação na requisição. A resposta da API é processada usando parseJsonSafe para retornar um objeto JavaScript com os dados do usuário ou um objeto vazio em caso de erro.
export async function apiGetMe(token) {
  return request('/auth/me', {
    method: 'GET',
    headers: buildHeaders({ tokenOverride: token }),
  });
}

export async function apiForgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ email }),
  });
}

export async function apiResetPassword(token, newPassword) {
  return request('/auth/reset-password', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function apiCreateEvent(payload, token) {
  return request('/events', {
    method: 'POST',
    headers: buildHeaders({ includeJson: true, tokenOverride: token }),
    body: JSON.stringify(payload),
  });
}

export async function apiGetEventBySlug(slug) {
  return request(`/events/${slug}`, {
    method: 'GET',
    headers: buildHeaders(),
  });
}

export async function apiCreateRsvp(slug, payload) {
  return request(`/events/${slug}/rsvp`, {
    method: 'POST',
    headers: buildHeaders({ includeJson: true }),
    body: JSON.stringify(payload),
  });
}

export async function apiListMyEvents(token) {
  return request('/events/mine', {
    method: 'GET',
    headers: buildHeaders({ tokenOverride: token }),
  });
}

export async function apiListEventGuests(eventId, token) {
  return request(`/events/${eventId}/guests`, {
    method: 'GET',
    headers: buildHeaders({ tokenOverride: token }),
  });
}

export async function apiPublishEvent(eventId, isPublished, token) {
  return request(`/events/${eventId}/publish`, {
    method: 'PATCH',
    headers: buildHeaders({ includeJson: true, tokenOverride: token }),
    body: JSON.stringify({ isPublished }),
  });
}

export async function apiUpdateEvent(eventId, payload, token) {
  return request(`/events/${eventId}`, {
    method: 'PATCH',
    headers: buildHeaders({ includeJson: true, tokenOverride: token }),
    body: JSON.stringify(payload),
  });
}

export async function apiDeleteEvent(eventId, token) {
  return request(`/events/${eventId}`, {
    method: 'DELETE',
    headers: buildHeaders({ tokenOverride: token }),
  });
}

export async function apiExportGuestsCsv(eventId, token) {
  return request(`/events/${eventId}/export`, {
    method: 'GET',
    headers: buildHeaders({ tokenOverride: token }),
    expectBlob: true,
  });
}

export async function apiSendReminders(eventId, token) {
  return request(`/events/${eventId}/reminders`, {
    method: 'POST',
    headers: buildHeaders({ tokenOverride: token }),
  });
}

export async function apiValidateRsvpProtocol(protocol) {
  return request(`/events/rsvp/${encodeURIComponent(protocol)}/validate`, {
    method: 'GET',
    headers: buildHeaders(),
  });
}

// Ambas as funções usam a função fetch para enviar os dados de email e senha no formato JSON e retornam a resposta da API como um objeto JavaScript usando res.json().

